import {createContext, memo, useCallback, use, useEffect, useMemo, useState} from 'react'
import {Button, FormControl, Textarea, TextInput} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {ScopedCommands} from '@github-ui/ui-commands'
import {
  useBatchComposition,
  useUnifiedBatch,
  useOptionalCommitMessageGenerationConfig,
  usePullRequestBasePath,
} from '../../../stores/layout-selectors'
import {useSuggestionCommitMessage} from '@github-ui/diff-lines/hooks/use-suggestion-commit-message'
import {createUnifiedBatchCommitMessageConfig} from '../../../helpers/create-batch-commit-message-config'
import {
  HeadOidMismatchError,
  useApplyUnifiedBatchSuggestionsMutation,
} from '../../../mutations/use-apply-unified-batch-suggestions-mutation'
import {useManageBatchTelemetry} from './manage-batch-telemetry'
import styles from './ManageBatchCommitTab.module.css'

/**
 * Fallback for batches that cannot generate a title or whose generation fails.
 */
export const DEFAULT_COMMIT_MESSAGE = 'Apply batched suggestions from code review'

const BASE_ERROR_MESSAGE = 'There was an error trying to commit changes'

type FormState = {
  commitMessage: string
  extendedDescription: string
}

/**
 * Shared state between the tab body (form fields) and the primary CTA in the
 * panel's actions row. The two render in separate parts of `ManageBatchPanel`,
 * so the form values, submission status, and error message live in a context
 * provider that wraps both.
 */
type ManageBatchCommitContextValue = {
  formValues: FormState
  setCommitMessage: (value: string) => void
  setExtendedDescription: (value: string) => void
  isSubmitting: boolean
  errorMessage: string | undefined
  submit: () => void
  canSubmit: boolean
  formDisabled: boolean
  inputRef: React.RefObject<HTMLInputElement | null>
  descriptionInputRef: React.RefObject<HTMLTextAreaElement | null>
  caption: string | undefined
  descriptionCaption: string | undefined
  placeholder: string | undefined
}

const ManageBatchCommitContext = createContext<ManageBatchCommitContextValue | undefined>(undefined)

function useManageBatchCommitContext(): ManageBatchCommitContextValue {
  const ctx = use(ManageBatchCommitContext)
  if (!ctx) {
    throw new Error('ManageBatchCommit components must be rendered inside <ManageBatchCommitProvider>.')
  }
  return ctx
}

/**
 * Provider for the Commit suggestions tab.
 *
 * Wraps both the body slot (`ManageBatchCommitTab`) and the footer primary
 * CTA slot (`ManageBatchCommitTabPrimaryCta`) so they can share form state
 * and the submit callback without prop drilling through `ManageBatchPanel`.
 *
 * Commits via `useApplyUnifiedBatchSuggestionsMutation`. On success it fires
 * success telemetry, calls `onSubmitted` so the panel can close itself, and then
 * reloads the page so the freshly committed suggestions appear in the diff.
 */
export const ManageBatchCommitProvider = memo(function ManageBatchCommitProviderImpl({
  children,
  onSubmitted,
  generationActive = false,
}: {
  children: React.ReactNode
  /** Called after a successful commit so the panel can close itself. */
  onSubmitted?: () => void
  generationActive?: boolean
}) {
  const batchComposition = useBatchComposition()
  const {onSubmit, onSubmitSuccess, onSubmitFailure, onHeadOidMismatch} = useManageBatchTelemetry()
  const {mutateAsync, isPending: isSubmitting} = useApplyUnifiedBatchSuggestionsMutation()
  const {items, revalidatedHeadSha} = useUnifiedBatch()
  const authorization = useOptionalCommitMessageGenerationConfig()
  const pullRequestPath = usePullRequestBasePath()
  const generationConfig = useMemo(
    () => createUnifiedBatchCommitMessageConfig(items, {authorization, pullRequestPath, headOid: revalidatedHeadSha}),
    [items, authorization, pullRequestPath, revalidatedHeadSha],
  )
  const {
    commitMessage,
    setCommitMessage,
    extendedDescription,
    setExtendedDescription,
    markSubmitted,
    inputRef,
    descriptionInputRef,
    caption,
    descriptionCaption,
    placeholder,
  } = useSuggestionCommitMessage({
    commentKind: 'ccr',
    generationConfig,
    fallbackCommitMessage: DEFAULT_COMMIT_MESSAGE,
    isActive: generationActive,
    isSubmitting,
  })
  const formValues = useMemo(() => ({commitMessage, extendedDescription}), [commitMessage, extendedDescription])
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const formDisabled = batchComposition !== 'suggestion-only'
  const canSubmit = !formDisabled && formValues.commitMessage.trim().length > 0 && !isSubmitting

  const submit = useCallback(async () => {
    if (!canSubmit) return

    markSubmitted()
    onSubmit('commit')
    setErrorMessage(undefined)

    const message = [formValues.commitMessage.trim(), formValues.extendedDescription.trim()]
      .filter(Boolean)
      .join('\n\n')

    try {
      await mutateAsync({message})

      // Run the success side effects after awaiting the mutation rather than in a
      // per-call `mutate(..., {onSuccess})` callback. react-query drops the per-call
      // callbacks when the calling component unmounts before the mutation settles —
      // e.g. the user closes the panel mid-commit — which would land the commit but
      // silently skip the reload and success telemetry. Awaiting keeps them on the
      // promise continuation so they always run, and lets us order telemetry +
      // onSubmitted before the page reload.
      onSubmitSuccess('commit')
      onSubmitted?.()
      ssrSafeWindow?.location.reload()
    } catch (error: unknown) {
      // A push landed after the batch was last revalidated, so the server rejected the stale head.
      // Counted separately from generic failures to measure how often this happens in practice.
      if (error instanceof HeadOidMismatchError) {
        onHeadOidMismatch()
      }
      onSubmitFailure('commit', error)
      const friendly = error instanceof Error ? error.message : undefined
      setErrorMessage(friendly ? `${BASE_ERROR_MESSAGE}: ${friendly}` : `${BASE_ERROR_MESSAGE}.`)
    }
  }, [
    canSubmit,
    markSubmitted,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    onHeadOidMismatch,
    onSubmitted,
    mutateAsync,
    formValues.commitMessage,
    formValues.extendedDescription,
  ])

  const value = useMemo<ManageBatchCommitContextValue>(
    () => ({
      formValues,
      setCommitMessage,
      setExtendedDescription,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
      formDisabled,
      inputRef,
      descriptionInputRef,
      caption,
      descriptionCaption,
      placeholder,
    }),
    [
      formValues,
      setCommitMessage,
      setExtendedDescription,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
      formDisabled,
      inputRef,
      descriptionInputRef,
      caption,
      descriptionCaption,
      placeholder,
    ],
  )

  return <ManageBatchCommitContext value={value}>{children}</ManageBatchCommitContext>
})

/**
 * Binds the `github:submit-form` command (⌘/Ctrl+Enter) to the Commit tab's
 * submit handler for its entire subtree, mirroring how `ApplySuggestionDialog`
 * wraps its form in `ScopedCommands` so the batch can be committed via the
 * keyboard while focus is anywhere in the form area.
 *
 * `ScopedCommands` renders a `display: contents` wrapper when no `className`
 * or `as` is supplied, so this does not affect the panel's flex layout.
 */
export const ManageBatchCommitFormCommands = memo(function ManageBatchCommitFormCommandsImpl({
  children,
}: {
  children: React.ReactNode
}) {
  const {submit} = useManageBatchCommitContext()
  return <ScopedCommands commands={{'github:submit-form': submit}}>{children}</ScopedCommands>
})

/**
 * Body content for the Commit suggestions tab.
 *
 * Renders the required `Commit message` text input and the optional
 * `Commit description` textarea. Mirrors the field layout of the per-comment
 * `ApplySuggestionDialog.tsx` so the unified panel feels consistent with the
 * legacy single-suggestion dialog.
 *
 * The submission error is rendered inline below the form via
 * `FormControl.Validation` — same pattern as `ApplySuggestionDialog.tsx`.
 */
export const ManageBatchCommitTab = memo(function ManageBatchCommitTabImpl() {
  const {
    formValues,
    setCommitMessage,
    setExtendedDescription,
    errorMessage,
    formDisabled,
    inputRef,
    descriptionInputRef,
    caption,
    descriptionCaption,
    placeholder,
  } = useManageBatchCommitContext()

  useEffect(() => {
    if (errorMessage) {
      announce(errorMessage)
    }
  }, [errorMessage])

  return (
    <div className={styles.form} data-testid="manage-batch-commit-tab">
      <FormControl required disabled={formDisabled}>
        <FormControl.Label>Commit message</FormControl.Label>
        <TextInput
          block
          name="commit_message"
          data-testid="manage-batch-commit-message-input"
          value={formValues.commitMessage}
          ref={inputRef}
          placeholder={placeholder}
          onChange={event => setCommitMessage(event.target.value)}
        />
        {caption && <FormControl.Caption>{caption}</FormControl.Caption>}
      </FormControl>
      <FormControl disabled={formDisabled}>
        <FormControl.Label>Commit description</FormControl.Label>
        <Textarea
          block
          rows={3}
          name="commit_description"
          data-testid="manage-batch-commit-description-input"
          value={formValues.extendedDescription}
          ref={descriptionInputRef}
          onChange={event => setExtendedDescription(event.target.value)}
        />
        {descriptionCaption && <FormControl.Caption>{descriptionCaption}</FormControl.Caption>}
      </FormControl>
      {errorMessage && (
        // The error is announced to screen readers via `announce()` from
        // `@github-ui/aria-live` (see the effect above); the inline
        // `FormControl.Validation` provides the visible message.
        <FormControl.Validation variant="error" className={styles.errorRow} data-testid="manage-batch-commit-error">
          {errorMessage}
        </FormControl.Validation>
      )}
    </div>
  )
})

/**
 * Primary CTA for the Commit suggestions tab footer slot.
 *
 * Renders the green `Commit suggestions` button; disabled when the batch is
 * not suggestion-only, when the commit message is blank, or while a submission is in
 * flight. Spinner mirrors the inline-spinner pattern used by the legacy
 * `ApplySuggestionDialog.tsx` `Commit changes` button.
 */
export const ManageBatchCommitTabPrimaryCta = memo(function ManageBatchCommitTabPrimaryCtaImpl() {
  const {submit, canSubmit, isSubmitting} = useManageBatchCommitContext()

  return (
    <Button
      variant="primary"
      onClick={submit}
      disabled={!canSubmit}
      loading={isSubmitting}
      data-testid="manage-batch-commit-cta"
    >
      Commit suggestions
    </Button>
  )
})
